import React, { useRef, useEffect, useState, useContext } from "react";
import {
  GreetingItem,
  GreetingTypes,
  SKGreetingArtifact,
} from "../../../../../models/SKArtifact";
import { CreateArtifactContext } from "../../../../../context/CreateArtifactContext";

interface AddImageWithTextProps {
  text: string;
  innerImage?: string;
}

const AddImageWithText: React.FC<AddImageWithTextProps> = ({
  text,
  innerImage,
}) => {
  const { contentData } = useContext(CreateArtifactContext);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [mainImage] = useState<HTMLImageElement>(new Image());
  const [greetingImage] = useState<HTMLImageElement>(new Image());
  const [greetingItems, setGreetingItems] = useState<GreetingItem>();

  useEffect(() => {
    const greetings = GreetingTypes.find(
      (item) =>
        item.greetingType === (contentData as SKGreetingArtifact).greetingType
    );
    if (greetings) {
      setGreetingItems(greetings);
      mainImage.src = greetings.greetingImgSrc;
      if (innerImage) {
        mainImage.src = "/images/greetings/emptyframe.png";
        greetingImage.src = innerImage;
      }
    }
  }, [contentData, innerImage]);

  useEffect(() => {
    const handleLoad = () => drawCanvas();

    mainImage.onload = handleLoad;
    greetingImage.onload = handleLoad;

    return () => {
      mainImage.onload = null;
      greetingImage.onload = null;
    };
  }, [mainImage, greetingImage, text, greetingItems]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        const mainImageAspectRatio = mainImage.width / mainImage.height;
        const canvasAspectRatio = canvasWidth / canvasHeight;

        let drawWidth = canvasWidth;
        let drawHeight = canvasHeight;
        if (mainImageAspectRatio > canvasAspectRatio) {
          drawHeight = canvasHeight;
          drawWidth = drawHeight * mainImageAspectRatio;
        } else {
          drawWidth = canvasWidth;
          drawHeight = drawWidth / mainImageAspectRatio;
        }
        const mainImageXOffset = (canvasWidth - drawWidth) / 2;
        const mainImageYOffset = (canvasHeight - drawHeight) / 2;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(
          mainImage,
          mainImageXOffset,
          mainImageYOffset,
          drawWidth,
          drawHeight
        );

        drawText(
          context,
          text,
          greetingItems?.greetingPosition ?? { x: 40, y: 25 }
        );

        if (greetingItems?.imagePosition && greetingImage.src) {
          const { x, y } = greetingItems.imagePosition;

          const greetingImageWidth = 90;
          const greetingImageHeight = 90;

          context.save();

          context.translate(
            x + greetingImageWidth / 2,
            y + greetingImageHeight / 2
          );

          const angle = Math.PI / -19;
          context.rotate(angle);

          context.drawImage(
            greetingImage,
            -greetingImageWidth / 2,
            -greetingImageHeight / 2,
            greetingImageWidth,
            greetingImageHeight
          );

          context.restore();

          const pinIcon = new Image();
          pinIcon.src = "/images/greetings/emptypin.png";

          pinIcon.onload = () => {
            const pinIconWidth = 30;
            const pinIconHeight = 30;

            const pinIconX = 80;
            const pinIconY = 8;

            context.drawImage(
              pinIcon,
              pinIconX,
              pinIconY,
              pinIconWidth,
              pinIconHeight
            );
          };
        }
      }
    }
  };

  const drawText = (
    context: CanvasRenderingContext2D,
    text: string,
    position: { x: number; y: number }
  ) => {
    const padding = 3;
    const maxWidth = 130;
    const lineHeight = 15;

    context.fillStyle = greetingItems?.greetingTextColor ?? "#ffffff";
    context.font = "18px Arial";

    const words = text.split(" ");
    let line = "";
    let y = position.y;

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + " ";
      const metrics = context.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth) {
        context.fillText(line, position.x + padding, y);
        line = words[i] + " ";
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    context.fillText(line, position.x + padding, y);
  };

  return (
    <div className="flex flex-col items-start">
      <canvas
        ref={canvasRef}
        width={200}
        height={200}
        className="mb-4 border-[#78788033] border-[2px] rounded-lg"
      />
    </div>
  );
};

export default AddImageWithText;
